import { boundCount, comboKey } from "@/domain/profile-resolver/index";
import type { CharBind, Layers, Profile } from "@/domain/types";
import { ingestDestiny } from "@/games/destiny/ingest";
import { parse, type SC2Hotkey, type SC2Table } from "@/games/sc2/parser/index";
import { bindingFor, hotkeyBindingCount, hotkeysLayers } from "@/games/sc2/parser/profile";
import { qwerty } from "@/hardware/keyboard-layouts/index";
import { fileStem } from "@/hardware/keyboards/keyboard-import";
import type { ProfileFormat } from "./detect";

/**
 * An uploaded Profile, one arm per export format. SC2 carries its positional [Hotkeys] decode and the
 * raw text the route re-parses when the Keyboard Layout switches (the file is character-space). A
 * simple Game (Destiny) carries the already-ingested character-space Profile plus the gameId the
 * route switches to; its ingest is layout-agnostic, so no re-parse is needed. Both keep `text` so a
 * reload can re-ingest, and `name` for the picker and toast.
 */
export type ImportedProfile =
  | {
      format: "sc2-hotkeys";
      /** The [Hotkeys] flatten parsed through QWERTY, for the preview's coverage stats only. */
      layers: Layers;
      /** The full parsed table; the preview counts it and the command card reads it. */
      table: SC2Table;
      name: string;
      text: string;
    }
  | {
      format: "destiny-cvars";
      /** The Game this upload selects (a simple Game's "my own" Profile slot). */
      gameId: string;
      /** The ingested character-space Profile - projected per view through the chosen lens. */
      profile: Profile;
      name: string;
      text: string;
    };

/**
 * Mappable [Commands] bindings in a table. A [Commands]-only delta export is a
 * real SC2 export and renders through the command card.
 */
export function commandBindingCount(table: SC2Table): number {
  return Object.values(table.commands)
    .flatMap((byUnit) => Object.values(byUnit))
    .filter((alternatives) => alternatives.length > 0).length;
}

/** What an uploaded .SC2Hotkeys file binds. */
export interface SC2BindingCounts {
  /** [Hotkeys] bindings keylens renders (curated in, at least one mappable alternative). */
  global: number;
  /** [Commands] bindings: the command cards. */
  card: number;
  /** global + card - the whole file, the honest answer to "how big is this setup". */
  total: number;
  /** Control groups the file binds (ControlGroupRecall0..9). */
  controlGroups: number;
  /** Bindings that live on a modifier combo, counted across BOTH sections. */
  modifier: number;
}

/**
 * The numbers the SC2 import preview and its toast report, computed once so the two cannot disagree
 * (the preview used to headline the [Hotkeys] flatten's positional cell count - 35 on a real
 * thousand-binding setup - while the toast reported a different globals figure again).
 *
 * Everything is counted per BINDING, the unit a player thinks in: one [Hotkeys] id or one
 * [Commands] bucket, however many alternate keys or physical cells it spreads over.
 */
export function sc2BindingCounts(table: SC2Table): SC2BindingCounts {
  const global = hotkeyBindingCount(table);
  const card = commandBindingCount(table);
  const onCombo = (alts: SC2Hotkey[]) => alts.some((a) => comboKey(a.mods) !== "");
  const modGlobals = Object.entries(table.hotkeys).filter(
    ([id, alts]) => bindingFor(id) != null && onCombo(alts),
  ).length;
  const modCards = Object.values(table.commands)
    .flatMap((byUnit) => Object.values(byUnit))
    .filter(onCombo).length;
  return {
    global,
    card,
    total: global + card,
    controlGroups: Object.keys(table.hotkeys).filter((id) => /^ControlGroupRecall\d$/.test(id))
      .length,
    modifier: modGlobals + modCards,
  };
}

/**
 * Distinct Commands a simple-Game Profile binds, across its base tiers and every Context. Counts by
 * Command identity (cmd, else name), so a movement bind overlaid onto the vehicle Context for
 * steering is not double-counted - the honest "how many actions did this file load" the toast reports
 * and the empty-file guard tests.
 */
export function profileBindingCount(profile: Profile): number {
  const cmds = new Set<string>();
  const add = (binds: CharBind[]) => {
    for (const cb of binds) cmds.add(cb.binding.cmd ?? cb.binding.name);
  };
  profile.base.forEach(add);
  for (const binds of Object.values(profile.contexts ?? {})) add(binds);
  return cmds.size;
}

/**
 * Single entry point for profile uploads: dispatch on the detected format, name from the file stem,
 * and refuse a file with no bindings. SC2 parses through QWERTY (a zone-only or [Commands]-only file
 * still imports - slice 19). Destiny ingests its cvars.xml through the one shared Destiny path into a
 * character-space Profile, board-portable like its built-ins. The overloads narrow the return arm to
 * a literal format, so a known-format caller reads `.table` / `.profile` with no manual narrowing.
 */
export function importProfile(
  text: string,
  opts: { fileName?: string; format: "sc2-hotkeys" },
): Extract<ImportedProfile, { format: "sc2-hotkeys" }>;
export function importProfile(
  text: string,
  opts: { fileName?: string; format: "destiny-cvars" },
): Extract<ImportedProfile, { format: "destiny-cvars" }>;
export function importProfile(
  text: string,
  opts: { fileName?: string; format: ProfileFormat },
): ImportedProfile;
export function importProfile(
  text: string,
  opts: { fileName?: string; format: ProfileFormat },
): ImportedProfile {
  const name = fileStem(opts.fileName) ?? "Uploaded profile";
  if (opts.format === "destiny-cvars") {
    const profile = ingestDestiny(text, { id: "uploaded", name });
    if (profileBindingCount(profile) === 0) {
      throw new Error("No bindings found. Is this a Destiny cvars.xml export?");
    }
    return { format: "destiny-cvars", gameId: "destiny-2", profile, name, text };
  }
  const table = parse(text, qwerty);
  const layers = hotkeysLayers(table);
  if (boundCount(layers) === 0 && commandBindingCount(table) === 0) {
    throw new Error("No bindings found. Is this a .SC2Hotkeys export?");
  }
  return { format: "sc2-hotkeys", layers, table, name, text };
}
